import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { Check, Zap, Star, ShieldCheck, History, ArrowRight, Clock, Lock, RefreshCw } from 'lucide-react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const PricingPlans = () => {
    const { subscription, user, refreshSubscription } = useAuth();
    const trialUsed = subscription?.trialUsed === true;
    const subscriptionExpired = subscription?.isExpired === true;

    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(true);
    const [payMsg, setPayMsg] = useState('');

    useEffect(() => {
        fetchPlans();
        fetchHistory();
    }, []);

    const fetchPlans = async () => {
        try {
            const [plansRes, configRes] = await Promise.all([
                api('/subscription-plans/active'),
                api('/subscriptions/config')
            ]);

            let allPlans = [];

            // 1. Add Trial Plan (from config) — always first
            if (configRes.ok) {
                const config = await configRes.json();
                allPlans.push({
                    _id: 'free_trial_config',
                    name: 'Free Trial',
                    price: 0,
                    durationDays: config.trialDurationDays,
                    maxUsers: config.maxUsers,
                    features: config.features,
                    isTrial: true
                });
            }

            // 2. Add active standard plans (isCustom filtered out by backend)
            if (plansRes.ok) {
                const data = await plansRes.json();
                allPlans = [...allPlans, ...data];
            }

            // 3. Mark active plan & handle current custom/exclusive plan as read-only card
            if (subscription) {
                let foundActive = false;
                allPlans = allPlans.map(p => {
                    if (
                        p.name === subscription.plan ||
                        (p.isTrial && subscription.plan === 'free_trial')
                    ) {
                        foundActive = true;
                        return { ...p, isCurrentPlan: true };
                    }
                    return p;
                });

                // If the tenant is on an exclusive custom plan, prepend it as read-only info
                if (!foundActive && subscription.plan !== 'free_trial') {
                    allPlans.unshift({
                        _id: 'custom_active_plan',
                        name: subscription.plan,
                        price: 'Exclusive',
                        durationDays: subscription.daysRemaining,
                        maxUsers: subscription.maxUsers,
                        features: subscription.features || [],
                        isCurrentPlan: !subscriptionExpired,
                        isExclusive: true,
                        isReadOnly: true,   // Cannot be re-purchased
                    });
                }
            }

            setPlans(allPlans);
        } catch (error) {
            console.error('Error fetching plans:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchHistory = async () => {
        try {
            const response = await api('/subscriptions/history');
            if (response.ok) {
                const data = await response.json();
                setHistory(data);
            }
        } catch (error) {
            console.error('Error fetching history:', error);
        } finally {
            setHistoryLoading(false);
        }
    };

    // ── Razorpay Payment Handler ───────────────────────────────────────────────
    const handlePay = async (plan) => {
        if (submitting) return;
        setSubmitting(true);
        setPayMsg('');

        try {
            // 1. Create Razorpay order
            const orderRes = await api('/payment/create-order', {
                method: 'POST',
                body: { planId: plan._id }
            });
            const orderData = await orderRes.json();

            if (!orderRes.ok) {
                setPayMsg(orderData.message || 'Failed to create payment order.');
                setSubmitting(false);
                return;
            }

            // 2. Open Razorpay checkout modal
            const options = {
                key: orderData.keyId,
                amount: orderData.amount,
                currency: orderData.currency,
                name: process.env.REACT_APP_APP_NAME || 'CRM Automax',
                description: `${plan.name} — ${plan.durationDays} days`,
                order_id: orderData.orderId,
                prefill: {
                    name: user?.name || '',
                    email: user?.email || '',
                },
                theme: { color: '#2563eb' },
                handler: async (response) => {
                    // 3. Verify payment on backend
                    try {
                        const verifyRes = await api('/payment/verify', {
                            method: 'POST',
                            body: {
                                razorpay_order_id: response.razorpay_order_id,
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_signature: response.razorpay_signature,
                                planId: plan._id,
                            }
                        });
                        const verifyData = await verifyRes.json();
                        if (verifyRes.ok && verifyData.success) {
                            setPayMsg(`✅ ${verifyData.message}`);
                            // Refresh subscription context + plan list
                            refreshSubscription();
                            setTimeout(() => {
                                setPayMsg('');
                                fetchPlans();
                                fetchHistory();
                            }, 2000);
                        } else {
                            setPayMsg(verifyData.message || 'Payment verification failed.');
                        }
                    } catch {
                        setPayMsg('Payment verified but server update failed. Please contact support.');
                    }
                    setSubmitting(false);
                },
                modal: {
                    ondismiss: () => {
                        setPayMsg('Payment cancelled.');
                        setSubmitting(false);
                    }
                }
            };

            const rzp = new window.Razorpay(options);
            rzp.on('payment.failed', (response) => {
                setPayMsg(`Payment failed: ${response.error.description}`);
                setSubmitting(false);
            });
            rzp.open();

        } catch (err) {
            setPayMsg('Error initiating payment. Please try again.');
            setSubmitting(false);
        }
    };

    const getPlanIcon = (plan) => {
        if (plan.isTrial)     return <Clock size={22} color="#2563eb" />;
        if (plan.isExclusive) return <Star size={22} color="#f59e0b" fill="#f59e0b" />;
        return <ShieldCheck size={22} color="#2563eb" />;
    };

    return (
        <MainLayout title="Pricing & Plans">
            <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
                <div style={{ textAlign: 'center', marginBottom: '50px' }}>
                    <h2 style={{ fontSize: '32px', fontWeight: '900', color: '#0f172a', marginBottom: '12px' }}>Subscription Dashboard</h2>
                    <p style={{ color: '#64748b', fontSize: '17px' }}>Manage your plan and track billing history</p>
                </div>

                {/* Payment status message */}
                {payMsg && (
                    <div style={{
                        textAlign: 'center', marginBottom: '24px',
                        padding: '14px 24px', borderRadius: '14px',
                        background: payMsg.startsWith('✅') ? '#f0fdf4' : '#fef2f2',
                        border: `1px solid ${payMsg.startsWith('✅') ? '#bbf7d0' : '#fecaca'}`,
                        color: payMsg.startsWith('✅') ? '#16a34a' : '#dc2626',
                        fontWeight: '600', fontSize: '15px',
                    }}>
                        {payMsg}
                    </div>
                )}

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '100px' }}>
                        <div style={{ fontSize: '16px', color: '#64748b' }}>Loading plans...</div>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', justifyContent: 'center', alignItems: 'stretch' }}>
                        {plans.map((plan) => {
                            // Is this the tenant's current active plan (not expired)?
                            const isActive = plan.isCurrentPlan && !subscriptionExpired;
                            // Is this the last plan but expired?
                            const isExpiredPlan = plan.isCurrentPlan && subscriptionExpired;
                            // Free trial already used
                            const isTrialUsed = plan.isTrial && trialUsed && !plan.isCurrentPlan;
                            // Custom/Exclusive read-only plan
                            const isReadOnly = plan.isReadOnly === true;
                            // Is this button disabled?
                            const isDisabled = submitting || isActive || isTrialUsed || isReadOnly;

                            // Button label logic
                            let btnLabel, btnBg, btnColor, btnBorder;
                            if (submitting && !isDisabled) {
                                btnLabel = 'Processing...';
                                btnBg = '#93c5fd'; btnColor = 'white'; btnBorder = 'none';
                            } else if (isActive) {
                                btnLabel = '✓ Current Plan';
                                btnBg = '#f1f5f9'; btnColor = '#94a3b8'; btnBorder = 'none';
                            } else if (isExpiredPlan) {
                                btnLabel = <><RefreshCw size={15} style={{ marginRight: '6px' }} /> Renew Plan</>;
                                btnBg = 'linear-gradient(135deg, #2563eb, #1d4ed8)'; btnColor = 'white'; btnBorder = 'none';
                            } else if (isTrialUsed) {
                                btnLabel = '✕ Trial Already Used';
                                btnBg = '#fef2f2'; btnColor = '#f87171'; btnBorder = '1px solid #fecaca';
                            } else if (isReadOnly) {
                                btnLabel = '🔒 Managed by Admin';
                                btnBg = '#fffbeb'; btnColor = '#d97706'; btnBorder = '1px solid #fef3c7';
                            } else {
                                btnLabel = <><Zap size={15} style={{ marginRight: '6px' }} /> {isExpiredPlan ? 'Renew' : 'Select Plan'}</>;
                                btnBg = '#0f172a'; btnColor = 'white'; btnBorder = 'none';
                            }

                            return (
                                <div key={plan._id} style={{
                                    width: '300px',
                                    background: 'white',
                                    borderRadius: '24px',
                                    border: isActive ? '2px solid #2563eb' : isExpiredPlan ? '2px solid #ef4444' : plan.isExclusive ? '2px solid #f59e0b' : '1px solid #e2e8f0',
                                    padding: '32px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    boxShadow: isActive ? '0 20px 25px -5px rgba(37,99,235,0.1)' : isExpiredPlan ? '0 20px 25px -5px rgba(239,68,68,0.08)' : '0 4px 6px -1px rgba(0,0,0,0.05)',
                                    position: 'relative',
                                    transition: 'transform 0.2s ease',
                                    minHeight: '480px',
                                    opacity: plan.isExclusive && subscriptionExpired ? 0.7 : 1,
                                }}>

                                    {/* Badge */}
                                    {isActive && (
                                        <div style={{ position: 'absolute', top: '-14px', left: '50%', transform: 'translateX(-50%)', background: '#2563eb', color: 'white', padding: '6px 16px', borderRadius: '100px', fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
                                            ✓ Currently Active
                                        </div>
                                    )}
                                    {isExpiredPlan && (
                                        <div style={{ position: 'absolute', top: '-14px', left: '50%', transform: 'translateX(-50%)', background: '#ef4444', color: 'white', padding: '6px 16px', borderRadius: '100px', fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
                                            ⚠ Expired — Renew Now
                                        </div>
                                    )}
                                    {plan.isExclusive && !isExpiredPlan && !isActive && (
                                        <div style={{ position: 'absolute', top: '-14px', left: '50%', transform: 'translateX(-50%)', background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: 'white', padding: '6px 16px', borderRadius: '100px', fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
                                            💎 Exclusive Plan
                                        </div>
                                    )}

                                    <div style={{ marginBottom: '28px' }}>
                                        <div style={{ width: '52px', height: '52px', borderRadius: '16px', background: plan.isExclusive ? '#fffbeb' : '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
                                            {getPlanIcon(plan)}
                                        </div>
                                        <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a', marginBottom: '6px' }}>{plan.name}</h3>
                                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                                            <span style={{ fontSize: '36px', fontWeight: '900', color: '#1e293b' }}>
                                                {typeof plan.price === 'number' ? `₹${plan.price}` : plan.price}
                                            </span>
                                            <span style={{ color: '#64748b', fontSize: '14px' }}>
                                                {typeof plan.price === 'number' ? `/${plan.durationDays}d` : ''}
                                            </span>
                                        </div>
                                        {isExpiredPlan && (
                                            <div style={{ marginTop: '8px', fontSize: '12px', color: '#ef4444', fontWeight: '600' }}>
                                                This plan has expired. Renew to restore access.
                                            </div>
                                        )}
                                    </div>

                                    <div style={{ padding: '12px 16px', background: plan.isExclusive ? '#fffbeb' : '#f8fafc', borderRadius: '14px', marginBottom: '28px', border: '1px solid ' + (plan.isExclusive ? '#fef3c7' : '#f1f5f9') }}>
                                        <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', marginBottom: '4px' }}>Team Capacity</div>
                                        <div style={{ fontSize: '16px', fontWeight: '800', color: '#1e293b' }}>{plan.maxUsers} User Seats</div>
                                    </div>

                                    <div style={{ flex: 1, marginBottom: '32px' }}>
                                        <div style={{ fontSize: '12px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '14px', letterSpacing: '0.05em' }}>Included Features:</div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                            {(plan.features || []).slice(0, 6).map((f, i) => (
                                                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '14px', color: '#475569', lineHeight: '1.4' }}>
                                                    <Check size={16} color="#22c55e" style={{ flexShrink: 0, marginTop: '2px' }} />
                                                    <span>{f}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div style={{ marginTop: 'auto' }}>
                                        <button
                                            onClick={() => !isDisabled && !isActive && handlePay(plan)}
                                            disabled={isDisabled}
                                            style={{
                                                width: '100%', padding: '14px',
                                                background: btnBg,
                                                color: btnColor,
                                                border: btnBorder || 'none',
                                                borderRadius: '14px',
                                                fontWeight: '800', fontSize: '15px',
                                                cursor: isDisabled ? 'default' : 'pointer',
                                                transition: 'all 0.2s ease',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                boxShadow: (!isDisabled && !isActive) ? '0 10px 15px -3px rgba(15,23,42,0.2)' : 'none',
                                            }}
                                        >
                                            {btnLabel}
                                        </button>

                                        {/* Footer note under button */}
                                        {isTrialUsed && (
                                            <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '12px', color: '#f87171' }}>
                                                Your free trial has been used. Choose a paid plan above.
                                            </div>
                                        )}
                                        {isReadOnly && (
                                            <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '12px', color: '#92400e' }}>
                                                This exclusive plan was assigned by your administrator.
                                            </div>
                                        )}
                                        {!isTrialUsed && !isReadOnly && !isActive && !isExpiredPlan && typeof plan.price === 'number' && plan.price > 0 && (
                                            <div style={{ textAlign: 'center', marginTop: '12px', fontSize: '11px', color: '#94a3b8' }}>
                                                Secure payment via Razorpay · INR
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* History Table */}
                <div style={{ marginTop: '80px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                        <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <History size={22} color="#64748b" />
                        </div>
                        <h3 style={{ fontSize: '24px', fontWeight: '900', color: '#1e293b' }}>Billing & Plan History</h3>
                    </div>

                    <div style={{ background: 'white', borderRadius: '24px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                        <th style={thStyle}>Date</th>
                                        <th style={thStyle}>Action</th>
                                        <th style={thStyle}>Plan Change</th>
                                        <th style={thStyle}>Details</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {historyLoading ? (
                                        <tr><td colSpan={4} style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading history...</td></tr>
                                    ) : history.length === 0 ? (
                                        <tr><td colSpan={4} style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>No history found</td></tr>
                                    ) : (
                                        history.map((h, i) => (
                                            <tr key={h._id} style={{ borderBottom: i === history.length - 1 ? 'none' : '1px solid #f1f5f9' }}>
                                                <td style={tdStyle}>{new Date(h.createdAt).toLocaleDateString()}</td>
                                                <td style={tdStyle}>
                                                    <span style={{
                                                        padding: '6px 12px', borderRadius: '100px', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase',
                                                        background: h.action === 'custom_assign' ? '#fffbeb' : h.action === 'payment' ? '#f0fdf4' : '#eff6ff',
                                                        color: h.action === 'custom_assign' ? '#92400e' : h.action === 'payment' ? '#16a34a' : '#1e40af',
                                                        border: '1px solid ' + (h.action === 'custom_assign' ? '#fef3c7' : h.action === 'payment' ? '#bbf7d0' : '#dbeafe')
                                                    }}>
                                                        {h.action.replace(/_/g, ' ')}
                                                    </span>
                                                </td>
                                                <td style={tdStyle}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                        <span style={{ color: '#94a3b8', fontSize: '13px' }}>{h.oldPlan || 'Start'}</span>
                                                        <ArrowRight size={14} color="#cbd5e1" />
                                                        <span style={{ fontWeight: '700', color: '#1e293b' }}>{h.newPlan}</span>
                                                    </div>
                                                </td>
                                                <td style={tdStyle}>
                                                    <div style={{ fontSize: '13px', color: '#64748b' }}>
                                                        {h.maxUsers} Seats · {h.notes || 'No notes'}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

const thStyle = { padding: '20px', fontSize: '11px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left' };
const tdStyle = { padding: '20px', fontSize: '14px', color: '#475569' };

export default PricingPlans;
